/**
 * IndexedDB-based storage for Double Ratchet session state.
 * Each conversation (myUserId + otherUserId) has its own serialised session.
 */

const DB_NAME = 'elefa_ratchet_sessions';
const DB_VERSION = 1;
const STORE_NAME = 'sessions';
const IDENTITY_STORE = 'identity';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(new Error('Failed to open ratchet session DB'));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(IDENTITY_STORE)) {
        db.createObjectStore(IDENTITY_STORE, { keyPath: 'id' });
      }
    };
  });
}

function sessionKey(myUserId: string, otherUserId: string): string {
  return `${myUserId}::${otherUserId}`;
}

// ─── Session persistence ───────────────────────────────────

export async function storeSession(
  myUserId: string,
  otherUserId: string,
  sessionJson: any,
  remoteIdentityJson: any,
): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STORE_NAME], 'readwrite');
    tx.objectStore(STORE_NAME).put({
      id: sessionKey(myUserId, otherUserId),
      session: sessionJson,
      remoteIdentity: remoteIdentityJson,
      updatedAt: Date.now(),
    });
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(new Error('Failed to store session')); };
  });
}

export async function loadSession(
  myUserId: string,
  otherUserId: string,
): Promise<{ session: any; remoteIdentity: any } | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STORE_NAME], 'readonly');
    const req = tx.objectStore(STORE_NAME).get(sessionKey(myUserId, otherUserId));
    req.onsuccess = () => {
      const data = req.result;
      resolve(data ? { session: data.session, remoteIdentity: data.remoteIdentity } : null);
    };
    req.onerror = () => reject(new Error('Failed to load session'));
    tx.oncomplete = () => db.close();
  });
}

export async function deleteSession(
  myUserId: string,
  otherUserId: string,
): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STORE_NAME], 'readwrite');
    tx.objectStore(STORE_NAME).delete(sessionKey(myUserId, otherUserId));
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(new Error('Failed to delete session')); };
  });
}

// ─── Identity persistence ──────────────────────────────────

export async function storeIdentity(userId: string, identityJson: any): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([IDENTITY_STORE], 'readwrite');
    tx.objectStore(IDENTITY_STORE).put({ id: userId, identity: identityJson, updatedAt: Date.now() });
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(new Error('Failed to store identity')); };
  });
}

export async function loadIdentity(userId: string): Promise<any | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([IDENTITY_STORE], 'readonly');
    const req = tx.objectStore(IDENTITY_STORE).get(userId);
    req.onsuccess = () => resolve(req.result?.identity ?? null);
    req.onerror = () => reject(new Error('Failed to load identity'));
    tx.oncomplete = () => db.close();
  });
}

export async function clearAllSessions(): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STORE_NAME, IDENTITY_STORE], 'readwrite');
    tx.objectStore(STORE_NAME).clear();
    tx.objectStore(IDENTITY_STORE).clear();
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(new Error('Failed to clear sessions')); };
  });
}
